import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchSettings, type SettingsSearchHit } from '@tenfour/contracts';
import { useAccount } from '@/modules/accounts/hooks';
import { ChromeIcon } from '@/components/ChromeIcon';
import { PageHeaderIcon } from './PageHeaderIcon';
import { settingValue } from './settingsSearchValues';
import { settingsGroupTone } from './settingsGroups';
import styles from './SettingsSearch.module.css';
import { useSettingsFieldFocus } from '@/pages/authenticated/settings/useSettingsFieldFocus';

interface SettingsSearchProps {
  /** Reads the shared account cache for row values; no new request. */
  accountId?: string | null;
  /**
   * Page ids this user can actually reach, from the same gate filter the rail
   * below uses. Without it the search would offer settings the rail is hiding.
   */
  visiblePages: readonly string[];
  /** Owner-gated rows do not render for a member, so their anchors never resolve. */
  isOwner: boolean;
  /** Called after a hit is chosen, so the mobile drawer can close itself. */
  onNavigate?: () => void;
  /** The rail is inert while the settings pane is off-screen. */
  tabIndex?: number;
}

/**
 * Search across every individual setting, not just the twelve pages.
 *
 * Results come from the shared catalog in @tenfour/contracts — the same list the
 * rail is built from and the command palette reads — so a result can never point
 * at a page that does not exist. Choosing one routes to the page and hands the
 * row anchor to the page through the URL hash; `useSettingAnchor` on the other
 * side scrolls it into view and flashes it.
 */
export function SettingsSearch({
  accountId,
  visiblePages,
  isOwner,
  onNavigate,
  tabIndex,
}: SettingsSearchProps) {
  // The same ['account', id] cache the rail's org row and every settings page
  // read, so a result's value cannot disagree with the page it points at.
  const fieldFocus = useSettingsFieldFocus();
  const { data: account } = useAccount(accountId ?? '');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const boxRef = useRef<HTMLDivElement>(null);

  const hits = useMemo(
    () => searchSettings(query, { visiblePages, isOwner, limit: 6 }),
    [query, visiblePages, isOwner],
  );

  useEffect(() => {
    setCursor(0);
  }, [query]);

  // A click anywhere else dismisses the results without clearing the query, so
  // returning to the field resumes where they left off.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const go = (hit: SettingsSearchHit) => {
    // `key` changes on every selection so choosing the same row twice still
    // scrolls and flashes it — react-router leaves location.hash untouched when
    // you navigate to the URL you are already on, so the hash alone is not a
    // signal the page can react to.
    navigate(hit.href, { state: { settingAnchorKey: Date.now() } });
    setOpen(false);
    setQuery('');
    onNavigate?.();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setQuery('');
      setOpen(false);
      return;
    }
    if (!hits.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => (c + 1) % hits.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (c - 1 + hits.length) % hits.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = hits[cursor];
      if (hit) go(hit);
    }
  };

  const showing = open && query.trim().length >= 2;
  const optionId = (i: number) => `settings-search-option-${i}`;

  return (
    <div className={styles.wrap} ref={boxRef} {...fieldFocus}>
      <div className={styles.field}>
        <ChromeIcon name="search" tone="muted" size={18} />
        <input
          type="search"
          className={styles.input}
          placeholder="Search settings"
          aria-label="Search settings"
          value={query}
          tabIndex={tabIndex}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={showing}
          aria-controls="settings-search-results"
          aria-autocomplete="list"
          // Without this, arrowing through results moves the highlight silently:
          // a screen reader has no way to know which option is current.
          aria-activedescendant={showing && hits.length ? optionId(cursor) : undefined}
        />
      </div>

      {showing ? (
        <div className={styles.results} id="settings-search-results" role="listbox">
          {hits.length === 0 ? (
            <p className={styles.empty}>No setting matches “{query.trim()}”.</p>
          ) : (
            hits.map((hit, i) => (
              <button
                key={hit.href}
                id={optionId(i)}
                type="button"
                role="option"
                aria-selected={i === cursor}
                className={[styles.hit, i === cursor ? styles.hitOn : ''].join(' ')}
                onMouseEnter={() => setCursor(i)}
                onClick={() => go(hit)}
              >
                <PageHeaderIcon pathname={hit.page.href} state="idle" className={styles.tileIcon} />
                <span className={styles.hitText}>
                  <span className={styles.hitLabel}>{hit.entry.label}</span>
                  <span className={styles.hitWhere}>
                    {hit.page.label}, {hit.entry.card}
                  </span>
                </span>
                {settingValue(hit.entry.anchor, account) ? (
                  <span className={styles.hitValue}>
                    {settingValue(hit.entry.anchor, account)}
                  </span>
                ) : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
